import { z } from 'zod';
import { weekOf } from './calendar/dates.js';
import { isoDateSchema } from './projects.js';
import { taskPrioritySchema, taskTypeSchema } from './tasks.js';

/** TASK-04: filters, sorting and grouping of the task list; also the URL state of the list. */

export const taskSortFieldSchema = z.enum([
  'key',
  'title',
  'status',
  'priority',
  'dueDate',
  'createdAt',
  'updatedAt',
  /** Board order: workflow order of the status, then the position in the column (KAN-02). */
  'board',
]);
export type TaskSortField = z.infer<typeof taskSortFieldSchema>;

export const sortDirectionSchema = z.enum(['asc', 'desc']);
export type SortDirection = z.infer<typeof sortDirectionSchema>;

export const taskGroupBySchema = z.enum(['status', 'assignee', 'priority']);
export type TaskGroupBy = z.infer<typeof taskGroupBySchema>;

/** `overdue`: due before today and not done; `week`: Monday to Sunday of the current week. */
export const dueFilterSchema = z.enum(['overdue', 'today', 'week', 'none']);
export type DueFilter = z.infer<typeof dueFilterSchema>;

/** Assignee filter values besides user ids. */
export const assigneeFilterSchema = z.union([z.uuid(), z.enum(['me', 'none'])]);

/** Lists arrive as `a,b` in query strings and as arrays from the router; both are accepted. */
export function queryList<T extends z.ZodType>(item: T) {
  return z.preprocess(
    (value) => (typeof value === 'string' ? value.split(',').filter((part) => part !== '') : value),
    z.array(item).max(50),
  );
}

const flag = z.preprocess(
  (value) => (value === 'true' ? true : value === 'false' ? false : value),
  z.boolean(),
);

export const taskListFilterSchema = z.object({
  status: queryList(z.uuid()).optional(),
  assignee: queryList(assigneeFilterSchema).optional(),
  label: queryList(z.uuid()).optional(),
  priority: queryList(taskPrioritySchema).optional(),
  type: taskTypeSchema.optional(),
  due: dueFilterSchema.optional(),
  dueFrom: isoDateSchema.optional(),
  dueTo: isoDateSchema.optional(),
  /** Free text: key, title, description (TASK-04). */
  q: z.string().trim().max(200).optional(),
  /** Subtasks as rows of their own (decision 3c); otherwise top-level tasks only. */
  subtasks: flag.optional(),
});
export type TaskListFilter = z.infer<typeof taskListFilterSchema>;

export const taskListQuerySchema = taskListFilterSchema.extend({
  sort: taskSortFieldSchema.default('key'),
  dir: sortDirectionSchema.default('desc'),
  /** Also count the visible tasks per status, regardless of the filters (WIP limits, KAN-05). */
  counts: flag.optional(),
});
export type TaskListQuery = z.infer<typeof taskListQuerySchema>;

/** Priority order for sorting and grouping (highest first when descending). */
export const priorityRank = { low: 0, medium: 1, high: 2, critical: 3 } as const;

/**
 * Due date condition of a filter, relative to `today`: a date range, tasks without due date,
 * or overdue (before today and not done). Explicit `dueFrom`/`dueTo` narrow the range.
 */
export type DueCondition =
  | { kind: 'range'; from: string | null; to: string | null }
  | { kind: 'none' }
  | { kind: 'overdue'; before: string };

export function dueCondition(
  filter: Pick<TaskListFilter, 'due' | 'dueFrom' | 'dueTo'>,
  today: string,
): DueCondition | null {
  const explicit = { from: filter.dueFrom ?? null, to: filter.dueTo ?? null };
  const narrow = (from: string, to: string) => ({
    kind: 'range' as const,
    from: explicit.from !== null && explicit.from > from ? explicit.from : from,
    to: explicit.to !== null && explicit.to < to ? explicit.to : to,
  });
  switch (filter.due) {
    case 'none':
      return { kind: 'none' };
    case 'overdue':
      return { kind: 'overdue', before: today };
    case 'today':
      return narrow(today, today);
    case 'week': {
      const week = weekOf(today);
      return narrow(week.from, week.to);
    }
    case undefined:
      return explicit.from === null && explicit.to === null ? null : { kind: 'range', ...explicit };
  }
}

// --- Free text ---------------------------------------------------------------------------------

/**
 * Prefix query for PostgreSQL full text search (`to_tsquery('german', …)`): every word becomes
 * `word:*`, joined with `&`. Only letters and digits survive, so the result is safe as a
 * parameter. Null when nothing searchable remains.
 */
export function toPrefixTsQuery(text: string): string | null {
  const words = text.match(/[\p{L}\p{N}]+/gu) ?? [];
  return words.length === 0
    ? null
    : words
        .slice(0, 10)
        .map((w) => `${w}:*`)
        .join(' & ');
}

// --- Global search (TASK-06) -------------------------------------------------------------------

export const searchQuerySchema = z.object({ q: z.string().trim().min(1).max(200) });

export const SEARCH_LIMIT = 20;

export const searchResponseSchema = z.object({
  tasks: z.array(
    z.object({
      id: z.uuid(),
      key: z.string(),
      title: z.string(),
      type: taskTypeSchema,
      status: z.object({ name: z.string(), color: z.string() }),
      project: z.object({ key: z.string(), name: z.string() }),
    }),
  ),
  projects: z.array(z.object({ id: z.uuid(), key: z.string(), name: z.string() })),
});
export type SearchResponse = z.infer<typeof searchResponseSchema>;

// --- Board (KAN-02) ----------------------------------------------------------------------------

/** Drop a card into a column: directly after or before a visible card, or at the end. */
export const boardMoveBodySchema = z.object({
  statusId: z.uuid(),
  afterTaskId: z.uuid().nullable().optional(),
  beforeTaskId: z.uuid().nullable().optional(),
});
export type BoardMoveBody = z.infer<typeof boardMoveBodySchema>;
